import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Phone, MessageCircle, Mail, CheckCircle2, X, CalendarClock, PenLine, AlarmClock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FollowupPopup = () => {
  const { token, isAuthenticated } = useAuth();
  const [queue, setQueue] = useState([]);
  const [mode, setMode] = useState('view'); // view | remark | reschedule
  const [remark, setRemark] = useState('');
  const [resched, setResched] = useState({ date: '', time: '' });
  const dismissedRef = useRef(new Set());

  const headers = { Authorization: `Bearer ${token}` };
  const current = queue[0];

  const fetchDue = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API_URL}/api/admin/followups/due`, { headers: { Authorization: `Bearer ${token}` } });
      const items = (r.data.due || []).filter((f) => !dismissedRef.current.has(f.followup_id));
      setQueue(items);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchDue();
    const iv = setInterval(fetchDue, 60000);
    return () => clearInterval(iv);
  }, [isAuthenticated, fetchDue]);

  const removeCurrent = () => {
    dismissedRef.current.add(current.followup_id);
    setQueue((q) => q.slice(1));
    setMode('view');
    setRemark('');
    setResched({ date: '', time: '' });
  };

  const handleComplete = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/followups/${current.followup_id}`, { status: 'completed', outcome: remark || undefined }, { headers });
      toast.success('✅ Follow-up completed!');
      removeCurrent();
    } catch { toast.error('Failed'); }
  };

  const handleDismiss = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/followups/${current.followup_id}/dismiss`, {}, { headers });
      toast.info(`⏰ ${r.data.reappears_in_minutes} minute baad phir yaad dilayenge`);
      removeCurrent();
    } catch { removeCurrent(); }
  };

  const handleSaveRemark = async () => {
    if (!remark.trim()) { toast.error('Remark likhen'); return; }
    try {
      if (current.lead?.lead_number) {
        await axios.put(`${API_URL}/api/admin/lead/${current.lead.lead_number}`, { remarks: remark }, { headers });
      }
      await axios.put(`${API_URL}/api/admin/followups/${current.followup_id}`, { notes: remark }, { headers });
      toast.success('✍ Remark saved!');
      setMode('view');
    } catch { toast.error('Failed'); }
  };

  const handleReschedule = async () => {
    if (!resched.date) { toast.error('Nayi date chunen'); return; }
    try {
      await axios.put(`${API_URL}/api/admin/followups/${current.followup_id}`, { date: resched.date, time: resched.time || null }, { headers });
      toast.success('📅 Follow-up rescheduled!');
      removeCurrent();
    } catch { toast.error('Failed'); }
  };

  if (!isAuthenticated || !current) return null;

  const lead = current.lead || {};
  const waNum = (lead.whatsapp || lead.mobile || '').replace(/\D/g, '');
  const waUrl = waNum ? `https://wa.me/${waNum.length === 10 ? '91' + waNum : waNum}` : null;

  const Info = ({ label, value }) => value ? (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-[11px] text-slate-500 whitespace-nowrap">{label}</span>
      <span className="text-[11px] font-semibold text-slate-800 text-right break-words">{value}</span>
    </div>
  ) : null;

  const ActBtn = ({ icon: Icon, label, onClick, href, color, testId }) => {
    const cls = `flex flex-col items-center gap-1 flex-1 py-2 rounded-xl text-[10px] font-bold ${color} active:scale-95 transition-transform`;
    const inner = (<><Icon size={16} />{label}</>);
    return href ? <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className={cls} data-testid={testId}>{inner}</a> : <button onClick={onClick} className={cls} data-testid={testId}>{inner}</button>;
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="followup-popup-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="followup-popup">
        {/* Header */}
        <div className={`px-5 py-4 text-white ${current.is_overdue ? 'bg-gradient-to-r from-red-600 to-red-500' : 'bg-gradient-to-r from-[#0A2463] to-[#247BA0]'}`}>
          <div className="flex items-center gap-2">
            <AlarmClock size={20} className="animate-pulse" />
            <div className="flex-1">
              <p className="text-xs font-semibold opacity-80">{current.is_overdue ? '🔴 OVERDUE FOLLOW-UP' : '⏰ FOLLOW-UP REMINDER'}</p>
              <h3 className="text-base font-bold leading-tight" data-testid="popup-title">{current.title}</h3>
            </div>
            {queue.length > 1 && <span className="bg-white/20 text-[10px] font-bold px-2 py-1 rounded-full">+{queue.length - 1} more</span>}
          </div>
          <p className="text-[11px] opacity-80 mt-1">📅 {current.date} {current.time || ''} • Priority: {current.priority}</p>
        </div>

        <div className="p-4 space-y-3">
          {/* Customer details */}
          <div className="bg-slate-50 rounded-2xl p-3">
            <Info label="👤 Customer" value={lead.name || current.lead_name} />
            <Info label="🏢 Company" value={lead.company} />
            <Info label="📱 Mobile" value={lead.mobile} />
            <Info label="💬 WhatsApp" value={lead.whatsapp} />
            <Info label="📧 Email" value={lead.email} />
            <Info label="🛠 Service" value={(lead.services || []).join(', ')} />
            <Info label="📝 Last Remark" value={lead.last_remark} />
            <Info label="🔁 Previous Follow-up" value={lead.previous_followup_date} />
            <Info label="👨‍💼 Assigned To" value={lead.assigned_to_name} />
            {current.notes && <Info label="🗒 Notes" value={current.notes} />}
          </div>

          {mode === 'view' && (
            <>
              <div className="flex gap-2">
                <ActBtn icon={Phone} label="Call" href={lead.mobile ? `tel:${lead.mobile}` : undefined} onClick={() => toast.info('Mobile number nahi hai')} color="bg-blue-50 text-[#0A2463]" testId="popup-call" />
                <ActBtn icon={MessageCircle} label="WhatsApp" href={waUrl || undefined} onClick={() => toast.info('Number nahi hai')} color="bg-green-50 text-green-700" testId="popup-whatsapp" />
                <ActBtn icon={Mail} label="Email" href={lead.email ? `mailto:${lead.email}` : undefined} onClick={() => toast.info('Email nahi hai')} color="bg-orange-50 text-orange-600" testId="popup-email" />
                <ActBtn icon={PenLine} label="Remark" onClick={() => setMode('remark')} color="bg-violet-50 text-violet-700" testId="popup-remark-btn" />
                <ActBtn icon={CalendarClock} label="Resched" onClick={() => setMode('reschedule')} color="bg-amber-50 text-amber-700" testId="popup-reschedule-btn" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleComplete} data-testid="popup-complete" className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 text-white font-bold text-sm py-3 rounded-xl active:scale-[0.98]">
                  <CheckCircle2 size={16} /> Mark Completed
                </button>
                <button onClick={handleDismiss} data-testid="popup-dismiss" className="px-4 flex items-center justify-center gap-1 bg-slate-100 text-slate-600 font-bold text-sm py-3 rounded-xl active:scale-[0.98]">
                  <X size={16} /> Dismiss
                </button>
              </div>
            </>
          )}

          {mode === 'remark' && (
            <div className="space-y-2">
              <textarea data-testid="popup-remark-input" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Remark likhen..." rows={3} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm" autoFocus />
              <div className="flex gap-2">
                <button onClick={handleSaveRemark} data-testid="popup-remark-save" className="flex-1 bg-[#0A2463] text-white font-bold text-sm py-3 rounded-xl">Save Remark</button>
                <button onClick={() => setMode('view')} className="px-4 bg-slate-100 text-slate-600 font-bold text-sm py-3 rounded-xl">Back</button>
              </div>
            </div>
          )}

          {mode === 'reschedule' && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input data-testid="popup-resched-date" type="date" value={resched.date} onChange={(e) => setResched({ ...resched, date: e.target.value })} className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm" />
                <input data-testid="popup-resched-time" type="time" value={resched.time} onChange={(e) => setResched({ ...resched, time: e.target.value })} className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleReschedule} data-testid="popup-resched-save" className="flex-1 bg-[#0A2463] text-white font-bold text-sm py-3 rounded-xl">📅 Reschedule</button>
                <button onClick={() => setMode('view')} className="px-4 bg-slate-100 text-slate-600 font-bold text-sm py-3 rounded-xl">Back</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowupPopup;
